import { useState } from 'react';
import { useTranslations } from 'next-intl';
import Button, { ButtonType } from '../Button/Button';
import Input from '../Input/Input';
import styles from './ForgotPasswordForm.module.scss';
import { forgotPasswordApi } from '@/helpers/auth';

export type ForgotPasswordFormValues = { email: string };

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const Highlight = (chunks: React.ReactNode) => <span style={{ fontWeight: 500 }}>{chunks}</span>;

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const t = useTranslations('Login');

  const isDisabled = !isValidEmail(email);

  const handleReset = async () => {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) return;

    try {
      setErrorMsg(undefined);
      setLoading(true);
      await forgotPasswordApi(trimmed);
      setSubmitted(true);
    } catch (err: any) {
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.form}>
      <form aria-label='Login form'>
        {!submitted ? (
          <>
            <Input
              id='email'
              name='email'
              type='email'
              label={t('Email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete='email'
              aria-invalid={
                  !email ? undefined : !isValidEmail(email) ? true : undefined
                }
              className={styles.loginInput}
              errorText={errorMsg}
              placeholder={t('Enter your email')}
            />

            <div className={styles.loginButtonWrap}>
              <Button
                type={ButtonType.PRIMARY_LIGHT}
                onClick={handleReset}
                disabled={isDisabled}
                loading={loading}
              >
                {t('Reset password')}
              </Button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className={styles.successMessage}>
              {t.rich('We’ve sent a link to email', {
                email,
                highlight: Highlight,
              })}
            </span>

          </div>
        )}

        <div className={styles.loginButtonWrap}>
          <Button
            type={ButtonType.SECONDARY_LIGHT}
            className={styles.backToLoginButton}
            iconPosition='left'
            iconName='chevron_left'
            href='/login'
          >
            {t('Back to login')}
          </Button>
        </div>
      </form>
    </div>
  );
}
